import React, { useState, useEffect } from 'react';
import { Modal } from '../../molecules';
import {
  UserSearchBar,
  UsersPageHeader,
  AccessDeniedMessage,
  ShadowBanForm,
  SuccessMessage,
  AccessDeniedModalContent,
  Pagination,
} from '../../molecules';
import { UsersList } from '../../organisms';
import { usersService, type User } from '../../../services/usersService';

export const UsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [requiredRoles, setRequiredRoles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string>('');
  const [accessDeniedRoles, setAccessDeniedRoles] = useState<string[]>([]);
  const [showShadowBanModal, setShowShadowBanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [shadowBanDate, setShadowBanDate] = useState<string>('');
  const [shadowBanLoading, setShadowBanLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const loadUsers = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await usersService.searchUsers(search, page, 20);
      setUsers(response.data);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setIsAccessDenied(true);
        setError(null);
        const roles =
          err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser', 'staff'];
        setRequiredRoles(Array.isArray(roles) ? roles : []);
      } else {
        let errorMessage = 'Kullanıcılar yüklenirken bir hata oluştu';
        if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
        setError(errorMessage);
        setIsAccessDenied(false);
      }
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1, searchQuery);
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadUsers(1, searchQuery);
  };

  const handlePageChange = (page: number) => {
    loadUsers(page, searchQuery);
  };

  const handleActivate = async (user: User) => {
    try {
      setActionLoading(user.id);
      await usersService.activateUser(user.id);
      await loadUsers(currentPage, searchQuery);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setShowAccessDeniedModal(true);
        setAccessDeniedMessage('Bu işlem için gerekli yetkilere sahip değilsiniz.');
        const roles =
          err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser'];
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
      } else {
        alert(err?.message || 'Kullanıcı aktifleştirilirken bir hata oluştu');
      }
      console.error('Failed to activate user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (user: User) => {
    try {
      setActionLoading(user.id);
      await usersService.deactivateUser(user.id);
      await loadUsers(currentPage, searchQuery);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setShowAccessDeniedModal(true);
        setAccessDeniedMessage('Bu işlem için gerekli yetkilere sahip değilsiniz.');
        const roles =
          err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser'];
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
      } else {
        alert(err?.message || 'Kullanıcı pasifleştirilirken bir hata oluştu');
      }
      console.error('Failed to deactivate user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendPasswordReset = async (user: User) => {
    try {
      setActionLoading(user.id);
      await usersService.sendPasswordResetEmail(user.id);
      setSuccessMessage('Şifre sıfırlama maili başarıyla gönderildi.');
      setShowSuccessModal(true);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setShowAccessDeniedModal(true);
        setAccessDeniedMessage('Bu işlem için gerekli yetkilere sahip değilsiniz.');
        const roles =
          err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser'];
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
      } else {
        setSuccessMessage(err?.message || 'Şifre sıfırlama maili gönderilirken bir hata oluştu');
        setShowSuccessModal(true);
      }
      console.error('Failed to send password reset:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleShadowBanClick = (user: User) => {
    setSelectedUser(user);
    if (user.shadowBannedUntil) {
      const date = new Date(user.shadowBannedUntil);
      setShadowBanDate(date.toISOString().slice(0, 16));
    } else {
      setShadowBanDate('');
    }
    setShowShadowBanModal(true);
  };

  const handleShadowBanSave = async () => {
    if (!selectedUser) return;

    try {
      setShadowBanLoading(true);
      const date = shadowBanDate ? new Date(shadowBanDate) : null;
      await usersService.updateShadowBanDate(selectedUser.id, date);
      setShowShadowBanModal(false);
      setSelectedUser(null);
      setShadowBanDate('');
      await loadUsers(currentPage, searchQuery);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setShowAccessDeniedModal(true);
        setAccessDeniedMessage('Bu işlem için gerekli yetkilere sahip değilsiniz.');
        const roles =
          err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser'];
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
      } else {
        alert(err?.message || 'Shadow ban tarihi güncellenirken bir hata oluştu');
      }
      console.error('Failed to update shadow ban:', err);
    } finally {
      setShadowBanLoading(false);
    }
  };

  const handleShadowBanCancel = () => {
    setShowShadowBanModal(false);
    setSelectedUser(null);
    setShadowBanDate('');
  };

  // Show AccessDenied in full screen like dashboard
  if (isAccessDenied) {
    return (
      <AccessDeniedMessage
        message="Bu kullanıcılara erişmek için gerekli yetkilere sahip değilsiniz."
        requiredRoles={requiredRoles}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <UsersPageHeader />

      <UserSearchBar
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onSearch={handleSearch}
      />

      {/* Users List */}
      {loading ? (
        <div className="text-center text-gray-500 py-8">Yükleniyor...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-8">Kullanıcı bulunamadı</div>
      ) : (
        <>
          <UsersList
            users={users}
            actionLoading={actionLoading}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onSendPasswordReset={handleSendPasswordReset}
            onShadowBanClick={handleShadowBanClick}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Shadow Ban Modal */}
      <Modal
        isOpen={showShadowBanModal}
        onClose={handleShadowBanCancel}
        title="Shadow Ban Tarihini Değiştir"
        size="medium"
      >
        {selectedUser && (
          <ShadowBanForm
            user={selectedUser}
            shadowBanDate={shadowBanDate}
            onShadowBanDateChange={setShadowBanDate}
            onSave={handleShadowBanSave}
            onCancel={handleShadowBanCancel}
            isLoading={shadowBanLoading}
          />
        )}
      </Modal>

      {/* Access Denied Modal */}
      <Modal
        isOpen={showAccessDeniedModal}
        onClose={() => setShowAccessDeniedModal(false)}
        title="Erişim Reddedildi"
        size="medium"
      >
        <AccessDeniedModalContent
          message={accessDeniedMessage}
          requiredRoles={accessDeniedRoles}
          onClose={() => setShowAccessDeniedModal(false)}
        />
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Bilgi"
        size="medium"
      >
        <SuccessMessage
          message={successMessage}
          onClose={() => setShowSuccessModal(false)}
        />
      </Modal>
    </div>
  );
};
